import {
  doc,
  query,
  where,
  getDoc,
  collection,
  onSnapshot,
  Unsubscribe,
} from "@angular/fire/firestore";
import {
  RiskAreaModel,
  SecurityRouteModel,
  PointOfInterestModel,
  SecurityRouteOptionModel,
  SecurityRouteDocument,
  SecurityLocationDocument,
  SecurityMappingActorModel,
  SecurityMappingCollection,
  SecurityRouteOptionDocument,
  SecurityRouteLegOptionsDocument,
  SecurityCountryDocument,
  SecurityCountryLocationModel,
} from "src/app/store/models/security-mapping.model";
import { inject, Injectable } from "@angular/core";
import { SecurityMappingMapService } from "./security-mapping-map.service";
import { TenantService } from "src/app/services/tenant-service/tenant.service";

type NewPointOfInterest = Omit<PointOfInterestModel, "id">;
type NewRiskArea = Omit<RiskAreaModel, "id">;
type NewSecurityRoute = Omit<SecurityRouteModel, "id">;

const COUNTRIES_COLLECTION = "countries";
const COLLECTION_NAMES: Record<SecurityMappingCollection, string> = {
  locations: "security_locations",
  routes: "security_routes",
};
// A document holds 1 MiB: above this (long routes with many stops) the roads not in
// use are saved without their turn-by-turn steps
const MAX_ROUTE_OPTIONS_SIZE = 600 * 1024;

// Firestore access for Security mapping, in the tenant's database, per country (shared
// by every site in it): countries/{countryId}/security_locations and
// countries/{countryId}/security_routes.
// Reads only: saves go through the Cloud Function endpoints (SecurityMappingDataService),
// which write the document and log the action to event_logs.
@Injectable({ providedIn: "root" })
export class SecurityMappingFirestoreService {
  private tenantService = inject(TenantService);
  private securityMappingMap = inject(SecurityMappingMapService);

  // ** Listen
  // Active documents of the country. Only server snapshots are passed on: the tenant
  // databases have no offline cache, so a cache snapshot (e.g. empty while offline)
  // must not replace the data kept on the device. Returns the unsubscribe function.
  public async Listen<
    T extends SecurityLocationDocument | SecurityRouteDocument,
  >(
    countryId: string,
    collectionType: SecurityMappingCollection,
    onChange: (documents: T[]) => void,
    onError: (error: unknown) => void,
  ): Promise<Unsubscribe> {
    const db = await this.tenantService.getMyDb();
    const activeDocuments = query(
      collection(db, this.getCollectionPath(countryId, collectionType)),
      where("isActive", "==", true),
    );
    return onSnapshot(
      activeDocuments,
      { includeMetadataChanges: true },
      (snapshot) => {
        if (snapshot.metadata.fromCache) return;
        onChange(snapshot.docs.map((snapshotDoc) => snapshotDoc.data() as T));
      },
      onError,
    );
  }
  // ** Listen

  // ** Documents → app models
  public ToPointOfInterest(
    document: SecurityLocationDocument,
  ): PointOfInterestModel {
    return {
      id: document.id,
      name: document.name,
      position: document.position,
      description: document.description,
      endDate: toDate(document.endDate),
      categoryType: document.categoryType,
      startDate: toDate(document.startDate),
      // Older items kept one file in attachment
      attachments:
        document.attachments ??
        (document.attachment ? [document.attachment] : []),
    };
  }

  public ToRiskArea(document: SecurityLocationDocument): RiskAreaModel {
    return {
      ...this.ToPointOfInterest(document),
      shape: document.shape ?? "radius",
      ...(document.shape === "perimeter"
        ? { perimeter: document.perimeter ?? [] }
        : { radiusInMeters: document.radiusInMeters ?? 0 }),
    };
  }

  // Needs the geometry library loaded (LoadGeometry) to decode the path
  public ToRoute(document: SecurityRouteDocument): SecurityRouteModel {
    const { encodedPath, ...routePath } = document.routePath;
    return {
      id: document.id,
      name: document.name,
      stops: document.stops,
      endPoint: document.endPoint,
      startPoint: document.startPoint,
      travelMode: document.travelMode,
      description: document.description,
      isSafestRoute: document.isSafestRoute,
      routePath: {
        ...routePath,
        path: google.maps.geometry.encoding
          .decodePath(encodedPath)
          .map((position) => position.toJSON()),
      },
      createdBy: {
        email: document.createdByEmail,
        name: document.createdByUserName,
        photoUrl: document.createdByPhotoUrl,
      },
      selectedOptionIndexes: document.selectedOptionIndexes ?? [],
      legOptions: (document.legOptions ?? []).map((leg) =>
        leg.options.map(({ encodedPath, ...option }) => ({
          ...option,
          path: google.maps.geometry.encoding
            .decodePath(encodedPath)
            .map((position) => position.toJSON()),
        })),
      ),
    };
  }
  // ** Documents → app models

  // ** App models → documents
  // existing: the saved document when editing, which keeps its created* fields
  public CreateLocationDocument(
    item: NewPointOfInterest | NewRiskArea,
    id: string,
    countryId: string,
    actor: SecurityMappingActorModel,
    existing: SecurityLocationDocument | null,
  ): SecurityLocationDocument {
    const riskArea = "shape" in item ? item : null;
    const now = new Date().toISOString();
    return {
      id,
      countryId,
      isActive: true,
      updatedAt: now,
      name: item.name,
      position: item.position,
      updatedByEmail: actor.email,
      description: item.description,
      categoryType: item.categoryType,
      updatedByUserName: actor.userName,
      kind: riskArea ? "area" : "point",
      createdAt: existing?.createdAt ?? now,
      createdByEmail: existing?.createdByEmail ?? actor.email,
      createdByUserName: existing?.createdByUserName ?? actor.userName,
      shape: riskArea?.shape ?? null,
      endDate: toIsoString(item.endDate),
      startDate: toIsoString(item.startDate),
      radiusInMeters:
        riskArea?.shape === "radius" ? (riskArea.radiusInMeters ?? 0) : null,
      perimeter:
        riskArea?.shape === "perimeter" ? (riskArea.perimeter ?? []) : null,
      attachments: item.attachments ?? [],
    };
  }

  // Needs the geometry library loaded (LoadGeometry) to encode the path
  public CreateRouteDocument(
    route: NewSecurityRoute,
    id: string,
    countryId: string,
    actor: SecurityMappingActorModel,
    existing: SecurityRouteDocument | null,
  ): SecurityRouteDocument {
    const { path, ...routePath } = route.routePath;
    const now = new Date().toISOString();
    return {
      id,
      countryId,
      isActive: true,
      updatedAt: now,
      name: route.name,
      stops: route.stops,
      endPoint: route.endPoint,
      startPoint: route.startPoint,
      travelMode: route.travelMode,
      updatedByEmail: actor.email,
      description: route.description,
      updatedByUserName: actor.userName,
      isSafestRoute: route.isSafestRoute,
      createdAt: existing?.createdAt ?? now,
      createdByEmail: existing?.createdByEmail ?? route.createdBy.email,
      createdByUserName: existing?.createdByUserName ?? route.createdBy.name,
      createdByPhotoUrl:
        existing?.createdByPhotoUrl ?? route.createdBy.photoUrl ?? null,
      routePath: {
        ...routePath,
        encodedPath: google.maps.geometry.encoding.encodePath(path),
      },
      selectedOptionIndexes: route.selectedOptionIndexes,
      legOptions: this.createLegOptionsDocuments(
        route.legOptions,
        route.selectedOptionIndexes,
      ),
    };
  }
  // ** App models → documents

  // Every suggested road, its path encoded like routePath
  private createLegOptionsDocuments(
    legOptions: SecurityRouteOptionModel[][],
    selectedOptionIndexes: number[],
  ): SecurityRouteLegOptionsDocument[] {
    const toDocument = (
      { path, ...option }: SecurityRouteOptionModel,
      keepSteps: boolean,
    ): SecurityRouteOptionDocument => ({
      ...option,
      steps: keepSteps ? option.steps : [],
      encodedPath: google.maps.geometry.encoding.encodePath(path),
    });
    const documents = legOptions.map((options) => ({
      options: options.map((option) => toDocument(option, true)),
    }));
    if (JSON.stringify(documents).length <= MAX_ROUTE_OPTIONS_SIZE) {
      return documents;
    }
    return legOptions.map((options, legIndex) => ({
      options: options.map((option, optionIndex) =>
        toDocument(option, optionIndex === selectedOptionIndexes[legIndex]),
      ),
    }));
  }

  // Loads the geometry library used to encode / decode route paths
  public async LoadGeometry(): Promise<void> {
    await this.securityMappingMap.ImportGeometryLibrary();
  }

  // Country + region the country's data is saved under (expiry reminder tapped);
  // null when the country has no document or no country location
  public async GetCountryLocation(
    countryId: string,
  ): Promise<SecurityCountryLocationModel | null> {
    const db = await this.tenantService.getMyDb();
    const snapshot = await getDoc(doc(db, COUNTRIES_COLLECTION, countryId));
    const country = snapshot.data() as SecurityCountryDocument | undefined;
    return country?.location?.country?.unique_id ? country.location : null;
  }

  private getCollectionPath(
    countryId: string,
    collectionType: SecurityMappingCollection,
  ): string {
    return [
      COUNTRIES_COLLECTION,
      countryId,
      COLLECTION_NAMES[collectionType],
    ].join("/");
  }
}

function toIsoString(date: Date | null | undefined): string | null {
  return date ? new Date(date).toISOString() : null;
}

function toDate(value: string | null): Date | null {
  return value ? new Date(value) : null;
}
